import { PhoneOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import { Empty, message, Space, Spin, Tag, Tooltip, Typography } from 'antd'
import moment from 'moment'
import React from 'react'
import arLogo from '../../images/icons/ar.svg'
import enLogo from '../../images/icons/en.svg'
import frLogo from '../../images/icons/fr.svg'
import itLogo from '../../images/icons/it.svg'
import elLogo from '../../images/icons/el.svg'
import nlLogo from '../../images/icons/nl.svg'
import plLogo from '../../images/icons/pl.svg'
import kuLogo from '../../images/icons/ku.svg'
import ckbLogo from '../../images/icons/ckb.svg'
import esLogo from '../../images/icons/es.svg'
import deLogo from '../../images/icons/de.svg'
import ptLogo from '../../images/icons/pt.svg'
import ruLogo from '../../images/icons/ru.svg'
import trLogo from '../../images/icons/tr.svg'
import zhLogo from '../../images/icons/zh.svg'
import { calculateMoney } from './currencies'
import PatientAppointmentsDetails from '../../components/dashboard/patients/Patient/components/PatientProfile/PatientAppointmentsDetails'
import MedicalHistoryPopover from '../../components/dashboard/patients/Patient/components/PatientMedical/MedicalHistoryPopover'
import UpdateReferenceIdButton from '../../components/dashboard/patients/Patient/components/PatientProfile/UpdateReferenceIdButton'
import { invoiceErrorMessage } from '@dentolize/clinic-mobile/src/shared/utils/invoiceErrorMessages'

export const cascadeFilter = (inputValue, path) =>
  path.some(option => option.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1)

export const renderOption = (item, t, insurance = false, user, forNewAppointment = false, onSelectPatient, handleSelectLead) => {
  if (item === 'LoadingDataList') {
    return {
      disabled: true,
      key: 'loading',
      value: 'loading',
      label: (
        <div key={item} className="center">
          <Spin />
        </div>
      )
    }
  } else if (item === 'EmptyDataList') {
    return {
      disabled: true,
      key: 'empty',
      value: 'empty',
      label: (
        <div key={item}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Typography.Text mark> {insurance ? t('calendar.noCompany') : t('calendar.noPatient')}</Typography.Text>}
          />
        </div>
      )
    }
  }
  return {
    key: item.id,
    value: item.id,
    disabled: forNewAppointment && item.firstName && item.type === 2,
    label: (
      <Space direction="vertical" key={item.id} size={2}>
        <Space size={3} wrap>
          <Typography.Text strong>{item.name || `${item.firstName} ${item.lastName}`}</Typography.Text>
          {item.firstName && item.type === 2 ? <Tag color="orange-inverse">{t('app.lead')}</Tag> : null}
          {item.referenceId ? <Tag type="secondary">#{item.referenceId}</Tag> : null}
          {item.patientId ? <Typography.Text type="secondary">{item.patientId}</Typography.Text> : null}
          {item.hasMedicalAlerts ? <MedicalHistoryPopover patientId={item.id} className="danger-text" /> : null}
        </Space>
        <Space size={3} wrap style={{ marginTop: 4 }}>
          <Typography.Text type="secondary">
            <PhoneOutlined /> {item.phoneNumber?.replace('_', ' ') || item.number?.replace('_', ' ') || ''}
          </Typography.Text>
          {item.prevAppointment || item.lastAppointment || item.activeAppointment ? (
            <PatientAppointmentsDetails patientDetails={item} />
          ) : null}
        </Space>
        {forNewAppointment && item.firstName && item.type === 2 ? (
          <UpdateReferenceIdButton handleSelectLead={handleSelectLead} onSelectPatient={onSelectPatient} patient={item} />
        ) : null}
        {/* Insurance Details */}
        {item.insuranceCompany ? (
          <Space size={0} wrap style={{ marginTop: 4 }}>
            <Tooltip title={t('calendar.insuranceCompany')}>
              <Tag color="cyan">
                <SafetyCertificateOutlined style={{ marginRight: 2 }} />
                {item.insuranceCompany.name}
              </Tag>
            </Tooltip>
            {item.insurancePolicy ? (
              <Tooltip title={t('settings.insurance.insurancePolicy')}>
                <Tag color="blue">{item.insurancePolicy.policyNumber}</Tag>
              </Tooltip>
            ) : null}
            {item.policyClass ? (
              <Tooltip title={t('settings.insurance.policyClass')}>
                <Tag color="blue">{item.policyClass.name}</Tag>
              </Tooltip>
            ) : null}
            {!(item.policyClass ? item.policyClass.insuranceUnlimited : item.insuranceUnlimited) ? (
              <Tooltip title={t('calendar.insuranceLimit')}>
                <Tag color="gold">
                  {calculateMoney((item.policyClass ? item.policyClass.insuranceLimit : item.insuranceLimit) || 0, user)}
                </Tag>
              </Tooltip>
            ) : (
              <Tooltip title={t('calendar.unlimited')}>
                <Tag color="purple">{t('calendar.unlimited')}</Tag>
              </Tooltip>
            )}
            <Tooltip title={t('settings.account.insurancePercentage')}>
              <Tag color="geekblue">
                {(item.policyClass ? item.policyClass.insurancePercentage : item.insurancePercentage) || 0}%
              </Tag>
            </Tooltip>
            {item.insuranceEndDate ? (
              <Tooltip title={`${t('app.expires')} ${moment(item.insuranceEndDate).format('ll')}`}>
                <Tag color={moment(item.insuranceEndDate).isBefore(moment()) ? 'red-inverse' : 'green-inverse'}>
                  {moment(item.insuranceEndDate).fromNow()}
                </Tag>
              </Tooltip>
            ) : null}
          </Space>
        ) : null}
      </Space>
    )
  }
}

export const NotFoundContent = (loading, searching, networkStatus) =>
  loading || searching || !(networkStatus === 7 || networkStatus === 8) ? (
    <div className="center">
      <Spin />
    </div>
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
  )

export const renderDateTooltip = (date, danger) =>
  date ? (
    <Tooltip className="tooltip-icon" title={moment(date).format('lll')}>
      <Typography.Text type={danger ? 'danger' : 'secondary'}>{moment(date).fromNow()}</Typography.Text>
    </Tooltip>
  ) : null

export const tableCreatedAt = (t, sorter = true, title) => ({
  title: title || t('common.created'),
  key: 'createdAt',
  render: item => (
    <Space direction="vertical" size={0}>
      <Typography.Text>{item.createdBy?.name ?? ''}</Typography.Text>
      <Tooltip className="tooltip-icon hide-on-print" title={moment(item.createdAt).format('lll')}>
        <small>
          <Typography.Text type="secondary">{moment(item.createdAt).fromNow()}</Typography.Text>
        </small>
      </Tooltip>
      <Typography.Text className="hide-except-on-print" type="secondary">
        {moment(item.createdAt).format('lll')}
      </Typography.Text>
    </Space>
  ),
  sorter
})

export const tableUpdatedAt = (t, sorter = true) => ({
  title: t('common.lastUpdated'),
  className: 'hide-on-print',
  key: 'updatedAt',
  render: item => (
    <Space direction="vertical" size={0}>
      <Typography.Text>{item.updatedBy ? item.updatedBy.name : ''}</Typography.Text>
      {item.updatedAt ? (
        <Tooltip className="tooltip-icon" title={moment(item.updatedAt).format('lll')}>
          <small>
            <Typography.Text type="secondary">{moment(item.updatedAt).fromNow()}</Typography.Text>
          </small>
        </Tooltip>
      ) : null}
    </Space>
  ),
  sorter
})

export const messagesWhen = t => [
  {
    name: <Typography.Text>{t('settings.messages.newPatient')}</Typography.Text>,
    value: 'PATIENT_CREATED',
    raw: t('settings.messages.newPatient'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.patientHolidays')}</Typography.Text>,
    value: 'EVENTS_DAY',
    raw: t('settings.messages.allPatients')
  },
  {
    name: <Typography.Text>{t('settings.messages.allPatientsBDay')}</Typography.Text>,
    value: 'BIRTHDAY_AT',
    raw: t('settings.messages.allPatientsBDay')
  },
  {
    name: <Typography.Text>{t('settings.messages.newInvoice')}</Typography.Text>,
    value: 'INVOICE_CREATED',
    raw: t('settings.messages.newInvoice'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.newQuotation')}</Typography.Text>,
    value: 'QUOTATION_CREATED',
    raw: t('settings.messages.newQuotation'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.newPayment')}</Typography.Text>,
    value: 'PAYMENT_CREATED',
    raw: t('settings.messages.newPayment'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.newPrescription')}</Typography.Text>,
    value: 'PRESCRIPTION_CREATED',
    raw: t('settings.messages.newPrescription'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.newFreeForm')}</Typography.Text>,
    value: 'FREE_FORM_CREATED',
    raw: t('settings.messages.newFreeForm'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.newLabOrder')}</Typography.Text>,
    value: 'LAB_ORDER_CREATED',
    raw: t('settings.messages.newLabOrder'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.newEncounter')}</Typography.Text>,
    value: 'PATIENT_ENCOUNTER_CREATED',
    raw: t('settings.messages.newEncounter'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.beforeOperations')}</Typography.Text>,
    value: 'OPERATION_EXPIRE_BEFORE',
    raw: t('settings.messages.beforeOperations')
  },
  {
    name: <Typography.Text>{t('settings.messages.anAppointment')}</Typography.Text>,
    value: 'APPOINTMENT_CREATED',
    raw: t('settings.messages.anAppointment'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.forAppointments')}</Typography.Text>,
    value: 'APPOINTMENT_AT',
    raw: t('settings.messages.forAppointments')
  },
  {
    name: <Typography.Text>{t('settings.messages.beforeAppointments')}</Typography.Text>,
    value: 'APPOINTMENT_BEFORE',
    raw: t('settings.messages.beforeAppointments')
  },
  {
    name: <Typography.Text>{t('settings.messages.appointmentTime')}</Typography.Text>,
    value: 'APPOINTMENT_TIME',
    raw: t('settings.messages.appointmentTime'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.appointmentConfirmed')}</Typography.Text>,
    value: 'APPOINTMENT_CONFIRMED',
    raw: t('settings.messages.appointmentConfirmed'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.appointmentChecked')}</Typography.Text>,
    value: 'APPOINTMENT_CHECKEDIN',
    raw: t('settings.messages.appointmentChecked'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.appointmentCompleted')}</Typography.Text>,
    value: 'APPOINTMENT_COMPLETED',
    raw: t('settings.messages.appointmentCompleted'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.appointmentCancelled')}</Typography.Text>,
    value: 'APPOINTMENT_CANCELED',
    raw: t('settings.messages.appointmentCancelled'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.appointmentNo')}</Typography.Text>,
    value: 'APPOINTMENT_NOSHOW',
    raw: t('settings.messages.appointmentNo'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.appointmentDelayed')}</Typography.Text>,
    value: 'APPOINTMENT_DELAYED',
    raw: t('settings.messages.appointmentDelayed'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.appointmentWaiting')}</Typography.Text>,
    value: 'APPOINTMENT_WAITING',
    raw: t('settings.messages.appointmentWaiting'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.appointmentInProgress')}</Typography.Text>,
    value: 'APPOINTMENT_INPROGRESS',
    raw: t('settings.messages.appointmentInProgress'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.aNote')}</Typography.Text>,
    value: 'NOTE_CREATED',
    raw: t('settings.messages.aNote'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.noteTime')}</Typography.Text>,
    value: 'NOTE_TIME',
    raw: t('settings.messages.noteTime'),
    tasks: true
  },
  {
    name: <Typography.Text>{t('settings.messages.beforeA')}</Typography.Text>,
    value: 'NOTE_BEFORE',
    raw: t('settings.messages.beforeA')
  },
  {
    name: <Typography.Text>{t('settings.messages.forNotes')}</Typography.Text>,
    value: 'NOTE_AT',
    raw: t('settings.messages.forNotes')
  }
]

export const showAuthError = (e, t, setLoginModalVisible) => {
  if (e?.message?.includes('Unique constraint failed on the fields: (`id`)')) {
    message.error(t('app.itemCreated'))
  } else if (e?.message?.includes('Unique constraint failed on the fields')) {
    message.error(t('app.itemExists'))
  } else if (e?.message?.includes('User is switched')) {
    message.error(t('app.userSwitched'))
  } else if (
    e?.message?.includes('Not Logged in') ||
    e?.message?.includes('An operation failed because it depends on one or more records that were required')
  ) {
    setLoginModalVisible('true')
  } else if (e?.message?.includes('Not Authorised') || e?.message?.includes('No Permission')) {
    // logged in, but not allowed this: the server says "Not Logged in" when the session has ended
    message.error(t('app.noPermission'))
  } else if (e?.message?.includes('[Client Error]') || e?.message?.includes('Drive storage quota has been exceeded')) {
    message.error(e.message.replace('[Client Error]', ''))
  } else if (e?.message?.includes('[Translate Error]')) {
    message.error(t(e.message.replace('[Translate Error]', '')))
  } else if (e?.message?.includes('OAuth 2 access token')) {
    message.error(t('app.authError'))
  } else if (invoiceErrorMessage(e, t)) {
    message.error(invoiceErrorMessage(e, t))
  } else {
    message.destroy('unknownError')
    message.error({ content: t('common.somethingWentWrong'), key: 'unknownError' })
  }
}

export const languagesArr = [
  { val: 'en', name: 'English', icon: enLogo },
  { val: 'ar', name: 'Arabic', icon: arLogo },
  { val: 'fr', name: 'French', icon: frLogo },
  { val: 'it', name: 'Italian', icon: itLogo },
  { val: 'el', name: 'Greek', icon: elLogo },
  { val: 'nl', name: 'Dutch', icon: nlLogo },
  { val: 'pl', name: 'Polish', icon: plLogo },
  { val: 'ku', name: 'Kurdish (Kurmanji)', icon: kuLogo },
  { val: 'ckb', name: 'Kurdish (Sorani)', icon: ckbLogo }
]

export const TenantLanguagesArr = [
  { val: 'en', name: 'English', icon: enLogo },
  { val: 'ar', name: 'Arabic', icon: arLogo },
  { val: 'fr', name: 'French', icon: frLogo },
  { val: 'it', name: 'Italian', icon: itLogo },
  // { val: 'el', name: 'Greek', icon: elLogo },
  // { val: 'nl', name: 'Dutch', icon: nlLogo },
  { val: 'es', name: 'Spanish', icon: esLogo },
  { val: 'de', name: 'Dutch', icon: deLogo },
  { val: 'pt', name: 'Portuguese', icon: ptLogo },
  { val: 'ru', name: 'Russian', icon: ruLogo },
  { val: 'tr', name: 'Turkish', icon: trLogo },
  { val: 'zh', name: 'Chiness', icon: zhLogo }
  // { val: 'pl', name: 'Polish', icon: plLogo },
  // { val: 'ku', name: 'Kurdish (Kurmanji)', icon: kuLogo },
  // { val: 'ckb', name: 'Kurdish (Sorani)', icon: ckbLogo }
]

export const dateRanges = t => [
  {
    key: 'today',
    label: <Typography.Text>{t('calendar.today')}</Typography.Text>,
    value: [moment().startOf('day'), moment().endOf('day')]
  },
  {
    key: 'yesterday',
    label: <Typography.Text>{t('calendar.yesterday')}</Typography.Text>,
    value: [
      moment()
        .startOf('day')
        .subtract(1, 'day'),
      moment()
        .endOf('day')
        .subtract(1, 'day')
    ]
  },
  {
    key: 'thisWeek',
    label: <Typography.Text>{t('calendar.thisWeek')}</Typography.Text>,
    value: [moment().startOf('week'), moment().endOf('week')]
  },
  {
    key: 'lastWeek',
    label: <Typography.Text>{t('calendar.lastWeek')}</Typography.Text>,
    value: [
      moment()
        .startOf('week')
        .subtract(1, 'week'),
      moment()
        .endOf('week')
        .subtract(1, 'week')
    ]
  },
  {
    key: 'thisMonth',
    label: <Typography.Text>{t('calendar.thisMonth')}</Typography.Text>,
    value: [moment().startOf('month'), moment().endOf('month')]
  },
  {
    key: 'lastMonth',
    label: <Typography.Text>{t('calendar.lastMonth')}</Typography.Text>,
    value: [
      moment()
        .subtract(1, 'month')
        .startOf('month'),
      moment()
        .subtract(1, 'month')
        .endOf('month')
    ]
  },
  {
    key: 'thisYear',
    label: <Typography.Text>{t('calendar.thisYear')}</Typography.Text>,
    value: [moment().startOf('year'), moment().endOf('year')]
  },
  {
    key: 'lastYear',
    label: <Typography.Text>{t('calendar.lastYear')}</Typography.Text>,
    value: [
      moment()
        .subtract(1, 'year')
        .startOf('year'),
      moment()
        .subtract(1, 'year')
        .endOf('year')
    ]
  },
  {
    key: 'all',
    label: <Typography.Text>{t('calendar.all')}</Typography.Text>,
    value: null
  }
]
